import React, {useCallback, useContext, useEffect, useMemo, useReducer, useState} from "react";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import BadgeTwoToneIcon from "@mui/icons-material/BadgeTwoTone";
import PersonAddTwoToneIcon from "@mui/icons-material/PersonAddTwoTone";
import SecurityTwoToneIcon from "@mui/icons-material/SecurityTwoTone";
import Grid from "@mui/material/Grid";
import {FormProvider, useForm} from "react-hook-form";
import {AnagraficaCompleta} from "../../../widgets/common/AnagraficaCompleta";
import {roles} from "../../../../utils/dictionaries/Roles";
import {AppContext} from "../../../../App";
import {initReferenteState, REFERENTE_INITIAL_STATE, ReferenteReducer} from "../../../../reducers/ReferenteReducer";
import {registerCompanyUser} from "../../../../services/ReferenteServices";
import {setOpenSnackbar} from "../../../../services/AppServices";
import {getAccountInfo, getCompanyInfo} from "../../../../utils/GenericUtils";
import AutocompleteSelect from "../../../widgets/inputs/AutocompleteSelect";
import CompanyFieldSection from "./CompanyFieldSection";

const getRoleLabel = (role) => roles[role]?.label || role?.replaceAll("ROLE_", "") || role;

const getRoleOptions = () => getMyRolesOptions().map((role) => ({
	value: role,
	label: getRoleLabel(role),
	description: roles[role]?.description || "--",
	color: roles[role]?.color || "var(--atl-blue)",
}));

const CreaUtente = ({classes}) => {
	const appContext = useContext(AppContext);
	const [state, dispatch] = useReducer(ReferenteReducer, REFERENTE_INITIAL_STATE, initReferenteState);
	const methods = useForm({mode: "onChange"});
	const [submit, setSubmit] = useState(false);
	const roleOptions = useMemo(() => getRoleOptions(), []);

	const onSubmit = useCallback((data) => {
		setSubmit(true);
		registerCompanyUser({
			...data,
			nomeCompany: getCompanyInfo().id,
		}, dispatch);
	}, [dispatch]);

	useEffect(() => {
		if (submit && !state.loadingRegisterUser && state.errorRegisterUser === "") {
			setSubmit(false);
			setOpenSnackbar("success", "Utente registrato con successo", appContext.dispatch);
			methods.reset();
		} else if (submit && !state.loadingRegisterUser && state.errorRegisterUser !== "") {
			setSubmit(false);
			setOpenSnackbar("error", state.errorRegisterUser, appContext.dispatch);
		}
	}, [submit, methods, appContext.dispatch, state.loadingRegisterUser, state.errorRegisterUser]);

	return (
		<section className={classes.panel}>
			<header className={classes.panelHeader}>
				<div>
					<Typography className={classes.panelTitle}>Crea utente</Typography>
					<Typography className={classes.panelSubtitle}>
						Registra una nuova utenza aziendale e assegna subito i ruoli operativi.
					</Typography>
				</div>
			</header>

			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<div className={classes.sectionStack}>
						<CompanyFieldSection
							classes={classes}
							icon={<BadgeTwoToneIcon fontSize="small"/>}
							subtitle="Informazioni personali e contatti del nuovo utente."
							title="Anagrafica utente"
						>
							<Grid container spacing={2.5} className={classes.legacyFormGrid}>
								<AnagraficaCompleta
									classes={classes}
									fieldSize="small"
									intro="--"
									moreInfo
									selectClassName={classes.companySelectField}
									solidField
									solidSelect
								/>
							</Grid>
						</CompanyFieldSection>

						<CompanyFieldSection
							classes={classes}
							icon={<SecurityTwoToneIcon fontSize="small"/>}
							subtitle="Permessi iniziali assegnati al nuovo account."
							title="Ruoli e permessi"
						>
							<AutocompleteSelect
								className={classes.companySelectField}
								control={methods.control}
								defaultValue={[]}
								error={methods.errors["roles"]}
								helperText={methods.errors["roles"] ? "Seleziona almeno un ruolo" : ""}
								label="Ruoli da assegnare"
								multiple
								name="roles"
								options={roleOptions}
								renderOptionContent={(option) => (
									<div>
										<Typography variant="body2" style={{fontWeight: 850}}>
											{option.label}
										</Typography>
										<Typography variant="caption" color="textSecondary">
											{option.description}
										</Typography>
									</div>
								)}
								renderTags={(value, getTagProps) => value.map((option, index) => (
									<Chip
										{...getTagProps({index})}
										className={classes.roleChip}
										key={option.value}
										label={option.label}
										size="small"
										style={{"--role-color": option.color}}
									/>
								))}
								rules={{required: true}}
								size="small"
							/>
						</CompanyFieldSection>
					</div>

					<div className={classes.actionRow}>
						<Button
							className={classes.actionButton}
							disabled={submit}
							disableElevation
							onClick={methods.handleSubmit(onSubmit)}
							size="small"
							startIcon={submit ? <CircularProgress size={16}/> : <PersonAddTwoToneIcon/>}
							type="submit"
							variant="contained"
						>
							Crea utente
						</Button>
					</div>
				</form>
			</FormProvider>
		</section>
	);
};

export function getMyRolesOptions() {
	let rolesOptions = [];
	const myRoles = getAccountInfo().roles;
	if (myRoles.includes("ROLE_SUPERADMIN") || myRoles.includes("ROLE_ADMIN")) {
		rolesOptions = Object.keys(roles);
	} else {
		Object.keys(roles).forEach((key) => {
			if (roles[key].visibility.includes("ALL") || roles[key].visibility?.some((visibilityRole) => myRoles?.some((roleMine) => roleMine === visibilityRole))) {
				rolesOptions.push(key);
			}
		});
	}

	return rolesOptions;
}

export default React.memo(CreaUtente);
